import React from 'react';
import { Button } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { TEXT, nightsCount, bookingsCount } from '../../config/listingPerformanceLabels';
import { Figure } from './parts';

/**
 * «أداء الوحدات» — the counted box on one logged change (tasks/change-impact-before-after.md): the nights either
 * side of it, by stay date. Every figure and date is the server's (changeImpactService); this does no date
 * arithmetic and draws no conclusion. It only counts, so it carries no colour, no arrow and no difference between
 * the two sides. A person concludes, in «النتيجة».
 */

const shortDate = (date) => formatDate(date, { year: undefined });

const PeriodHead = ({ label, side }) => (
  <th scope="col">
    {label} (<bdi>{nightsCount(side.nights)}</bdi>)
    <span className="lp-impact__range lp-muted">{shortDate(side.from)} {TEXT.impactRangeTo} {shortDate(side.lastNight)}</span>
  </th>
);

/** The total, then each platform as recorded: nights, and how many bookings they came from. */
const SoldCell = ({ side }) => (
  <td>
    <span className="lp-impact__figure"><Figure value={side.sold} quietZero /></span>
    {side.byPlatform.length > 0 && (
      <ul className="lp-impact__platforms">
        {side.byPlatform.map(p => (
          <li key={p.platform}>
            <bdi>{getPlatformDisplayName(p.platform)}</bdi> <bdi className="lp-num">{p.nights}</bdi>{' '}
            <span className="lp-impact__count">(<bdi>{bookingsCount(p.bookings)}</bdi>)</span>
          </li>
        ))}
      </ul>
    )}
  </td>
);

const ChangeImpact = ({ impact, onWriteResult }) => {
  if (!impact.ready) {
    return (
      <p className="lp-impact__wait lp-muted">
        {/* With its year: a review date can run into next year, and this is the row's only word on when. */}
        <i className="bi bi-hourglass-split" aria-hidden="true" /> {TEXT.impactReadyOn} {formatDate(impact.readyOn)}
      </p>
    );
  }
  const { before, after, otherChanges } = impact;
  return (
    <section className="lp-impact" aria-label={TEXT.impactTitle}>
      <div>
        <p className="lp-impact__title">{TEXT.impactTitle}</p>
        <p className="lp-muted">{TEXT.impactNote}</p>
      </div>
      <table className="lp-table lp-impact__table">
        <thead>
          <tr>
            <th scope="col"><span className="visually-hidden">{TEXT.metric}</span></th>
            <PeriodHead label={TEXT.impactBefore} side={before} />
            <PeriodHead label={TEXT.impactAfter} side={after} />
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{TEXT.impactSold}</th>
            <SoldCell side={before} />
            <SoldCell side={after} />
          </tr>
          {after.sold > 0 && (
            <tr>
              <th scope="row">{TEXT.impactBookedBefore}</th>
              <td><span className="lp-missing">—</span></td>
              <td><Figure value={after.bookedBeforeChange} quietZero /></td>
            </tr>
          )}
          <tr>
            <th scope="row">{TEXT.impactClosed}</th>
            <td><Figure value={before.closed} quietZero /></td>
            <td><Figure value={after.closed} quietZero /></td>
          </tr>
          {/* A night with no calendar record is unknown, never zero: said only when there is one. */}
          {(before.unknown > 0 || after.unknown > 0) && (
            <tr>
              <th scope="row">{TEXT.impactUnknown}</th>
              <td><Figure value={before.unknown} quietZero /></td>
              <td><Figure value={after.unknown} quietZero /></td>
            </tr>
          )}
        </tbody>
      </table>
      {otherChanges.length > 0 && (
        <p className="lp-impact__other">{TEXT.impactOtherChanges}: {otherChanges.map(shortDate).join('، ')}</p>
      )}
      {impact.due && (
        <Button variant="outline-primary" size="sm" className="lp-impact__action" onClick={onWriteResult}>
          <i className="bi bi-pencil-square" aria-hidden="true" /> {TEXT.writeResult}
        </Button>
      )}
    </section>
  );
};

export default ChangeImpact;
